import { UserInfo } from "../../application/UserProfile";
import {
  FormSubmitArgs,
  FormViewConfig,
  FormViewService,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
  vsServiceName as FormViewServiceName,
} from "../../records/forms/formView";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../records/forms/formly/fieldConfigurationHelpers";
import { RecordDataSource } from "../../records/grids";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../records/grids/columns/columnDefinitionService";
import {
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
} from "../../records/grids/columns/columnDefinitionSetService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../records/server/serverUtility";
import {
  GridConfiguration,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../records/views/recordGridView";
import fieldHelpers from "../../ui-formly/fieldHelpers";
import { Role } from "../model/authorityGroup";

export const vsObjectName = "role";

roleObjectConfig.$inject = [
  RecordGridViewServiceProviderName,
  FormViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
];
export function roleObjectConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  formViewServiceProvider: FormViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  configureGrid(
    recordGridViewServiceProvider,
    columnDefinitionSetServiceProvider
  );
  configureForm(formViewServiceProvider);
}

function configureForm(formViewServiceProvider: FormViewServiceProvider) {
  formViewServiceProvider.addFormViewConfig(vsObjectName, [
    FormViewServiceName,
    FieldConfigurationHelperServiceName,
    (
      formViewService: FormViewService,
      fieldConfigurationHelperService: FieldConfigurationHelperService
    ): FormViewConfig => {
      return {
        fields: [
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: "RoleName",
                label: "Name",
                required: true,
              }),
              fieldHelpers.simpleStringField({
                key: "Description",
                label: "Description",
                required: true,
              }),
              fieldConfigurationHelperService.selectAsyncField({
                key: "AuthorityGroupId",
                name: "AuthorityGroupId",
                label: "Authority Group",
                required: true,
                getDefaultItem: (model: Role) => {
                  return {
                    Id: model.AuthorityGroupId,
                    Name: model.AuthorityGroup?.Name ?? "",
                  };
                },
                queryUrl: "/api/authorityGroup",
              }),
            ],
            4
          ),
        ],
        name: vsObjectName,
        title: (model: Role) => `Edit Role ${model.RoleName}`,
        submitForm: (args: FormSubmitArgs) => {
          const model = <Role>args.model;
          const isExistingRole = !!model.Id;
          const url = `/api/role${isExistingRole ? `/${model.Id}` : ""}`;
          const method = isExistingRole ? "put" : "post";

          return formViewService.submitForm<UserInfo>({
            formObject: args.model,
            formViewController: args.formViewController,
            url,
            method,
          });
        },
      };
    },
  ]);
}

function configureGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsObjectName, [
    ColumnDefinitionServiceName,
    (columnDefinitionService: ColumnDefinitionService) => [
      columnDefinitionService.getStringColDef({
        field: "AuthorityGroupName",
        header: "AuthorityGroup",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "RoleName",
        header: "Role Name",
      }),
      columnDefinitionService.getStringColDef({
        field: "RoleDescription",
        header: "Role Description",
      }),
      columnDefinitionService.getBooleanColDef({
        field: "SystemRole",
      }),
    ],
  ]);

  recordGridViewServiceProvider.addGridConfiguration(vsObjectName, [
    ServerUtilityName,
    (serverUtility: ServerUtility): GridConfiguration => {
      return {
        columnDefinitionSet: vsObjectName,
        defaultSort: "AuthorityGroupName,RoleName",
        gridDataSource: new RecordDataSource(serverUtility, "/api/role"),
        nameSingular: "Role",
        showAddButton: true,
        showAddButtonPermission: "userModify",
      };
    },
  ]);
}
